import React, { useState } from "react";
import { useMutation, gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../core/auth";
import BackArrow from "../../../components/common/back-arrow";
import FadeInWrapper from "../../../components/common/fade-in-wrapper";
import { track } from "@amplitude/analytics-browser";
import { notifyError } from "../../../utils/toast";
import { getErrorMessage } from "../../../utils/error";

// GraphQL Mutation for Seller Login
const SELLER_LOGIN = gql`
  mutation SellerLogin(
    $contactNumber: String!
    $password: String!
    $push_token: String
  ) {
    sellerLogin(
      contactNumber: $contactNumber
      password: $password
      push_token: $push_token
    ) {
      token
      error
    }
  }
`;

const BossLogin: React.FC = () => {
  const navigate = useNavigate();
  const { loginAdmin } = useAuth();
  const [contactNumber, setContactNumber] = useState("");
  const [password, setPassword] = useState("");
  const [login, { loading }] = useMutation(SELLER_LOGIN);

  const push_token = localStorage.getItem("expo_push_token") as string | null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await login({
        variables: { contactNumber, password, push_token },
      });

      const { token, error } = response.data.sellerLogin;

      if (error) {
        notifyError(error);
      } else {
        localStorage.setItem("sellerToken", token);

        await loginAdmin();
        navigate("/boss/home");
      }
    } catch (err) {
      console.error("Error during login:", err);
      notifyError(getErrorMessage(err));
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <FadeInWrapper className="w-full max-w-md bg-white p-6 pb-10">
        <div className="mb-4" onClick={() => navigate(-1)}>
          <BackArrow />
        </div>

        <div className="mb-6 text-center">
          <h2 className="text-2xl font-semibold text-black mb-2">사장님 로그인</h2>
          <p className="text-sm text-[#757575]">계정에 로그인하세요</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="contactNumber"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                연락처
              </label>
              <input
                type="number"
                id="contactNumber"
                inputMode="numeric"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-primary text-gray-700"
                placeholder="연락처를 입력하세요"
                required
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                비밀번호
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-primary text-gray-700"
                placeholder="비밀번호를 입력하세요"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            className={`w-full py-3 rounded-xl text-white font-bold ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary hover:brightness-90 transition duration-150"
            }`}
            disabled={loading}
          >
            {loading ? "로그인 중..." : "로그인"}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate("/boss/login/password-reset")}
              className="text-sm underline text-black hover:text-primary"
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>

          <div className="space-y-4">
            <button
              type="button"
              onClick={() => {
                track("소개서 상담 클릭");
                navigate("/boss/applicant/onboarding");
              }}
              className="w-full py-3 border border-gray-500 font-semibold bg-white rounded-xl transition duration-150 active:brightness-90"
            >
              소개서 받아보기
            </button>
            <button
              type="button"
              onClick={() => {
                track("사장님 입점신청1 입점신청 하기 클릭");
                navigate("/boss/signup/register");
              }}
              className="w-full py-3 bg-success border border-gray-500 text-black font-semibold rounded-xl transition duration-150 active:brightness-90"
            >
              입점신청 하기
            </button>
            <button
              type="button"
              onClick={() => {
                track("사장님 입점신청1 입점신청 현황 조회 클릭");
                navigate("/boss/signup/state");
              }}
              className="w-full py-3 border border-gray-500 font-semibold bg-secondary rounded-xl transition duration-150 active:brightness-90"
            >
              입점신청 현황 조회
            </button>
            <div className="bg-gray-100 p-4 rounded-xl break-keep text-sm text-gray-700">
              "입점신청 현황 조회"에서는 입점신청을 이어서 진행할 수 있습니다.
            </div>
          </div>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-sm underline text-[#757575] hover:text-primary"
          >
            그냥 둘러볼게요~!
          </button>
        </div>
      </FadeInWrapper>
    </div>
  );
};

export default BossLogin;
